import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Eye, EyeOff, Pencil, X } from "lucide-react";

const inputCls = "w-full bg-surface border border-ink/20 focus:border-ember outline-none px-3 py-2.5 text-ink placeholder:text-ink/30 transition-colors text-sm";
const labelCls = "block text-[11px] uppercase tracking-[0.2em] text-ink/50 mb-1.5";

const EMPTY = { name: "", role: "", text: "", order: 0, published: true };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/testimonials");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      if (msg) toast.success(msg);
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const submit = (e) => {
    e.preventDefault();
    if (editing) {
      run(async () => {
        await api.put(`/admin/testimonials/${editing.id}`, form);
        setEditing(null);
        setForm(EMPTY);
      }, "Témoignage modifié");
    } else {
      run(async () => {
        await api.post("/admin/testimonials", form);
        setForm(EMPTY);
      }, "Témoignage publié");
    }
  };

  const toggle = (it) => run(() => api.put(`/admin/testimonials/${it.id}`, { name: it.name, role: it.role, text: it.text, order: it.order, published: !it.published }), it.published ? "Témoignage dépublié" : "Témoignage publié");

  const remove = (id) => {
    if (!window.confirm("Supprimer ce témoignage ?")) return;
    run(() => api.delete(`/admin/testimonials/${id}`), "Témoignage supprimé");
  };

  return (
    <div className="mt-10 grid lg:grid-cols-3 gap-8" data-testid="admin-testimonials">
      <form onSubmit={submit} className="border border-ink/10 bg-surface p-6 h-fit" data-testid="testimonial-form">
        <h3 className="font-display font-medium text-ink">{editing ? "Modifier le témoignage" : "Ajouter un témoignage"}</h3>
        <div className="mt-5 space-y-4">
          <div><label className={labelCls}>Nom affiché</label><input data-testid="testi-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={inputCls} /></div>
          <div><label className={labelCls}>Précision (optionnel)</label><input data-testid="testi-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Étudiant, entrepreneure…" className={inputCls} /></div>
          <div><label className={labelCls}>Témoignage</label><textarea data-testid="testi-text" rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} required className={inputCls} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Ordre</label><input data-testid="testi-order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className={inputCls} /></div>
            <label className="flex items-end gap-2 pb-2 text-sm text-ink/70 cursor-pointer">
              <input data-testid="testi-published" type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 accent-[#C6552B]" />
              Publié
            </label>
          </div>
          <div className="flex gap-2">
            <button data-testid="testi-submit" disabled={busy} className="btn-glow flex-1 bg-ink hover:bg-ember disabled:opacity-50 text-white px-4 py-3 text-sm transition-colors">
              {editing ? "Enregistrer" : "Publier"}
            </button>
            {editing && (
              <button type="button" onClick={() => { setEditing(null); setForm(EMPTY); }} className="border border-ink/20 text-ink/60 px-4 py-3 text-sm"><X size={14} /></button>
            )}
          </div>
        </div>
      </form>

      <div className="lg:col-span-2 space-y-4">
        {items.length === 0 && <p className="text-ink/40 text-sm">Aucun témoignage pour l'instant.</p>}
        {items.map((it) => (
          <div key={it.id} className={`border border-ink/10 bg-surface p-6 flex items-start gap-4 ${!it.published ? "opacity-55" : ""}`} data-testid={`admin-testi-${it.id}`}>
            <div className="flex-1">
              <p className="text-ink/75 text-sm leading-relaxed">« {it.text} »</p>
              <p className="mt-3 text-sm font-medium text-ink">
                {it.name} {it.role && <span className="text-ink/40 font-normal">— {it.role}</span>}
                <span className="ml-3 text-xs text-ink/35">ordre {it.order} · {it.published ? "publié" : "non publié"}</span>
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setEditing(it); setForm({ name: it.name, role: it.role, text: it.text, order: it.order, published: it.published }); }} className="p-2 border border-ink/15 text-ink/50 hover:text-ink transition-colors" data-testid={`edit-testi-${it.id}`}><Pencil size={13} /></button>
              <button onClick={() => toggle(it)} className="p-2 border border-ink/15 text-ink/50 hover:text-ember transition-colors" data-testid={`toggle-testi-${it.id}`}>{it.published ? <EyeOff size={13} /> : <Eye size={13} />}</button>
              <button onClick={() => remove(it.id)} className="p-2 border border-ink/15 text-ink/50 hover:text-red-500 hover:border-red-300 transition-colors" data-testid={`delete-testi-${it.id}`}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
